/**
 * Billing & Payment Service
 * Handles Razorpay integration for AXOIX subscriptions
 */

import apiClient, { ApiError } from "./client";
import { ENDPOINTS } from "./config";

// ==================== INTERFACES ====================

export interface PricingTier {
  id: string;
  name: string;
  tagline: string;
  prices: {
    monthly: number | null;
    quarterly: number | null;
    six_month: number | null;
    annual: number | null;
    lifetime: number | null;
  };
  currency: string;
  features: Record<string, unknown>;
  color: string;
  icon: string;
  tier_level: number;
}

export interface CreateOrderRequest {
  tenant_id: string;
  tier_id: string;
  billing_cycle: "monthly" | "quarterly" | "six_month" | "annual" | "lifetime";
  addon_verticals?: string[];  // List of addon vertical IDs for pricing calculation
  /** Pre-launch unlock token from ?unlock= — see getSaleUnlockToken(). */
  unlock?: string;
}

export interface CreateOrderResponse {
  order_id: string;
  amount: number; // in paisa
  currency: string;
  tier_name: string;
  billing_cycle: string;
  razorpay_key_id: string;
}

export interface VerifyPaymentRequest {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  tenant_id: string;
}

export interface VerifyPaymentResponse {
  success: boolean;
  message: string;
  tenant_id: string;
  tier_id: string;
  billing_cycle: string;
  expires_at: string | null;
  commission_processed: boolean;
}

// Cycles that recur as real Razorpay Subscriptions (auto-charge each cycle).
// Five-year/ten-year stay one-time Orders — large prepaid commitments, not a
// cadence to auto-renew years later.
export const RECURRING_BILLING_CYCLES = ["monthly", "quarterly", "six_month", "annual"] as const;
export type RecurringBillingCycle = typeof RECURRING_BILLING_CYCLES[number];

export const isRecurringBillingCycle = (cycle: string): cycle is RecurringBillingCycle =>
  (RECURRING_BILLING_CYCLES as readonly string[]).includes(cycle);

export interface CreateSubscriptionRequest {
  tenant_id: string;
  tier_id: string;
  billing_cycle: RecurringBillingCycle;
  /** Pre-launch unlock token from ?unlock= — see getSaleUnlockToken(). */
  unlock?: string;
}

export interface CreateSubscriptionResponse {
  subscription_id: string;
  amount: number; // in paisa, GST-inclusive — matches what the Plan charges
  currency: string;
  tier_name: string;
  razorpay_key_id: string;
}

export interface VerifySubscriptionRequest {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
  tenant_id: string;
}

export interface VerifySubscriptionResponse {
  success: boolean;
  message: string;
  tenant_id: string;
  tier_id: string;
  status: string;
  dashboard_url: string;
}

export interface SandboxActivateRequest {
  tenant_id: string;
  tier_id: string;
  billing_cycle: "monthly" | "quarterly" | "six_month" | "annual" | "lifetime";
}

export interface SandboxActivateResponse {
  success: boolean;
  message: string;
  tenant_id: string;
  tier_id: string;
  tier_name: string;
  billing_cycle: string;
  expires_at: string | null;
  dashboard_url: string;
  is_sandbox: boolean;
  sandbox_payment_id: string;
}

export interface SubscriptionStatus {
  tenant_id: string;
  tier_id: string;
  tier_name: string;
  billing_cycle: string;
  status: string;
  expires_at: string | null;
  is_active: boolean;
  days_remaining: number | null;
  feature_limits: Record<string, unknown>;
}

export interface PaymentRecord {
  id: string;
  order_id: string;
  amount: number;
  tier_id: string;
  billing_cycle: string;
  status: string;
  created_at: string;
}

export interface PaymentMode {
  /** "test" while a rzp_test_ key is configured, "live" for rzp_live_. */
  mode: "test" | "live";
  /** False when no Razorpay key is set at all — checkout cannot run. */
  configured: boolean;
  key_id: string;
  /** False in test mode: a charge raises no invoice and posts no voucher. */
  books_enabled: boolean;
  /** Storefront is open to everyone (operator switch + live key + configured). */
  public_sale: boolean;
  /**
   * Whether THIS visitor may reach checkout — public_sale, or a valid
   * ?unlock= token during a pre-launch live test. The only field any UI
   * should gate on. Enforced server-side too, so it is not just a label.
   */
  sale_open: boolean;
}

/**
 * The pre-launch unlock token from the URL, persisted for the session so it
 * survives the pricing → signup → checkout hops (the query string does not).
 * Rotate or clear it from Mothership → PUT /api/billing/sale-status.
 */
export const SALE_UNLOCK_STORAGE_KEY = "axoix_sale_unlock";

export const getSaleUnlockToken = (): string | undefined => {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("unlock");
    if (fromUrl && fromUrl.trim()) {
      sessionStorage.setItem(SALE_UNLOCK_STORAGE_KEY, fromUrl.trim());
      return fromUrl.trim();
    }
    return sessionStorage.getItem(SALE_UNLOCK_STORAGE_KEY) || undefined;
  } catch {
    return undefined; // private-mode sessionStorage throws; unlock is optional
  }
};

// ==================== API FUNCTIONS ====================

/**
 * Which Razorpay account the platform is wired to right now.
 *
 * Paid plans must not be offered to the public while this reads "test": a
 * test key accepts Razorpay's test cards, so any visitor could complete
 * checkout for ₹0 and walk away with a provisioned paid tenant.
 *
 * Deliberately unauthenticated — `key_id` already reaches every browser that
 * opens a checkout, so this exposes nothing new.
 *
 * Callers MUST fail closed: if this throws, treat paid plans as unavailable.
 * A network blip is not a reason to start selling on a test key.
 */
export const getPaymentMode = async (unlock?: string): Promise<PaymentMode> => {
  const response = await apiClient.get<PaymentMode>(
    unlock
      ? `${ENDPOINTS.BILLING.PAYMENT_MODE}?unlock=${encodeURIComponent(unlock)}`
      : ENDPOINTS.BILLING.PAYMENT_MODE
  );
  return response.data;
};

/**
 * Get all public pricing tiers
 */
export const getPricingTiers = async (): Promise<PricingTier[]> => {
  try {
    const response = await apiClient.get<PricingTier[]>(ENDPOINTS.BILLING.PRICING);
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to fetch pricing tiers");
  }
};

/**
 * Create a Razorpay order for subscription
 */
export const createOrder = async (data: CreateOrderRequest): Promise<CreateOrderResponse> => {
  try {
    // Attach the unlock token here rather than at every call site — the
    // server rejects a paid order without it while the storefront is closed,
    // and a forgotten token would surface as a confusing 403.
    const response = await apiClient.post<CreateOrderResponse>(
      ENDPOINTS.BILLING.CREATE_ORDER,
      { ...data, unlock: data.unlock ?? getSaleUnlockToken() }
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to create payment order");
  }
};

/**
 * Verify Razorpay payment and upgrade subscription
 */
export const verifyPayment = async (data: VerifyPaymentRequest): Promise<VerifyPaymentResponse> => {
  try {
    const response = await apiClient.post<VerifyPaymentResponse>(
      ENDPOINTS.BILLING.VERIFY_PAYMENT,
      data
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Payment verification failed");
  }
};

/**
 * Create a real Razorpay Subscription (recurring mandate) — for monthly,
 * quarterly, six_month, and annual billing, which auto-renew. Five-year and
 * ten-year billing stays on createOrder.
 */
export const createSubscription = async (data: CreateSubscriptionRequest): Promise<CreateSubscriptionResponse> => {
  try {
    const response = await apiClient.post<CreateSubscriptionResponse>(
      ENDPOINTS.BILLING.CREATE_SUBSCRIPTION,
      { ...data, unlock: data.unlock ?? getSaleUnlockToken() }  // see createOrder
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to create subscription");
  }
};

/**
 * Cancel a Subscription that was created but never charged — the modal was
 * dismissed, or the browser tab closed, before a successful payment.
 *
 * Best-effort, deliberately fire-and-forget from the caller's side: the
 * mothership's 5-minute sweep (`cancel_abandoned_subscriptions`) already
 * cancels the same subscription if this call never lands, so a failure here
 * is not a user-facing error, just a slower cleanup. The endpoint only
 * cancels a subscription still sitting in some tenant's
 * `settings.pending_subscription` — it cannot touch a paid mandate by
 * guessing an id, since that field is cleared the moment a charge succeeds.
 */
export const cancelSubscription = async (subscriptionId: string): Promise<void> => {
  try {
    await apiClient.post(ENDPOINTS.BILLING.CANCEL_SUBSCRIPTION, {
      subscription_id: subscriptionId,
    });
  } catch (error) {
    console.warn("[Razorpay] cancelSubscription (best-effort) failed, 5-min sweep will catch it:", error);
  }
};

/**
 * Confirm the first charge of a new Subscription right after checkout.
 * Every charge after this is automatic — no frontend call needed ever again.
 */
export const verifySubscription = async (data: VerifySubscriptionRequest): Promise<VerifySubscriptionResponse> => {
  try {
    const response = await apiClient.post<VerifySubscriptionResponse>(
      ENDPOINTS.BILLING.VERIFY_SUBSCRIPTION,
      data
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Subscription verification failed");
  }
};

/**
 * Sandbox/Demo activation - DISABLED
 * All payments must go through Razorpay
 */
export const sandboxActivate = async (_data: SandboxActivateRequest): Promise<SandboxActivateResponse> => {
  throw new Error("Demo mode is disabled. Please complete payment via Razorpay.");
};

/**
 * Check if we're in sandbox mode - ALWAYS RETURNS FALSE
 * Demo mode is permanently disabled
 */
export const isSandboxMode = (_razorpayKeyId: string | undefined): boolean => {
  return false; // Never use sandbox mode
};

/**
 * Get subscription status for a tenant
 */
export const getSubscriptionStatus = async (tenantId: string): Promise<SubscriptionStatus> => {
  try {
    const response = await apiClient.get<SubscriptionStatus>(
      `${ENDPOINTS.BILLING.SUBSCRIPTION}/${tenantId}/subscription`
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to fetch subscription status");
  }
};

/**
 * Get payment history for a tenant
 */
export const getPaymentHistory = async (tenantId: string): Promise<{ payments: PaymentRecord[]; total_payments: number }> => {
  try {
    const response = await apiClient.get<{ payments: PaymentRecord[]; total_payments: number }>(
      `${ENDPOINTS.BILLING.PAYMENTS}/${tenantId}/payments`
    );
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to fetch payment history");
  }
};

// ==================== RAZORPAY HELPERS ====================

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id?: string;         // one-time Orders
  subscription_id?: string;  // recurring Subscriptions — mutually exclusive with order_id
  recurring?: boolean;
  handler: (response: RazorpayResponse | RazorpaySubscriptionResponse) => void;
  redirect?: boolean;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: Record<string, string>;
  theme?: {
    color?: string;
    backdrop_color?: string;
  };
  modal?: {
    ondismiss?: () => void;
    escape?: boolean;
    animation?: boolean;
    confirm_close?: boolean;
  };
}

interface RazorpayResponse {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpaySubscriptionResponse {
  razorpay_subscription_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}

interface RazorpayPaymentFailedEvent {
  error: {
    code: string;
    description: string;
    source?: string;
    step?: string;
    reason?: string;
  };
}

interface RazorpayInstance {
  open: () => void;
  close: () => void;
  on: (event: "payment.failed", callback: (response: RazorpayPaymentFailedEvent) => void) => void;
}

/**
 * Load Razorpay SDK script
 */
export const loadRazorpayScript = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Check if already loaded
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

/**
 * Open Razorpay payment modal
 */
export const openRazorpayCheckout = async (
  orderData: CreateOrderResponse,
  userInfo: {
    name: string;
    email: string;
    phone?: string;
  },
  callbacks: {
    onSuccess: (response: RazorpayResponse) => void;
    onDismiss?: () => void;
    onError?: (error: unknown) => void;
  },
  /**
   * What the sheet shows as the line description, when "<tier> - <cycle>" does
   * not describe the purchase. The /shop-checkout page passes the mothership's
   * own description: a usage pack has neither a tier nor a billing cycle, so
   * the default would render "undefined - undefined".
   */
  descriptionOverride?: string
): Promise<void> => {
  // Load Razorpay SDK
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    callbacks.onError?.(new Error("Failed to load Razorpay SDK"));
    return;
  }

  // Open checkout
  const options: RazorpayOptions = {
    key: orderData.razorpay_key_id,
    amount: orderData.amount,
    currency: orderData.currency,
    name: "AXOIX",
    description: descriptionOverride || `${orderData.tier_name} - ${orderData.billing_cycle}`,
    order_id: orderData.order_id,
    handler: callbacks.onSuccess as unknown as (response: RazorpayResponse | RazorpaySubscriptionResponse) => void,
    prefill: {
      name: userInfo.name,
      email: userInfo.email,
      contact: userInfo.phone,
    },
    notes: {
      tier: orderData.tier_name,
      billing_cycle: orderData.billing_cycle,
    },
    redirect: false,
    theme: {
      color: "#00ff97",
      backdrop_color: "rgba(10, 25, 47, 0.9)",
    },
    modal: {
      ondismiss: callbacks.onDismiss,
      escape: false,
      animation: true,
      confirm_close: true,
    },
  };

  try {
    const razorpay = new window.Razorpay(options);
    // The try/catch below only covers synchronous errors from construction
    // or open(). A declined card or failed UPI happens later, inside the
    // still-open modal, and Razorpay reports it as a "payment.failed" event —
    // not a thrown error and not modal.ondismiss (the user didn't close it,
    // Razorpay did). Without this listener the page never learns the payment
    // failed: it just sits on the Pay button while Razorpay's own email says
    // "Payment Failed" to the customer.
    razorpay.on("payment.failed", (response) => {
      callbacks.onError?.(new Error(response.error?.description || "Payment failed"));
    });
    razorpay.open();
  } catch (error) {
    callbacks.onError?.(error);
  }
};

/**
 * Open Razorpay in recurring-Subscription mode — same modal, but tied to a
 * Subscription mandate instead of a one-time Order, so the customer's card/UPI
 * gets saved for automatic future charges. Used for monthly/quarterly/
 * six_month/annual; five_year/ten_year still use openRazorpayCheckout.
 */
export const openRazorpaySubscriptionCheckout = async (
  subscriptionData: CreateSubscriptionResponse,
  userInfo: {
    name: string;
    email: string;
    phone?: string;
  },
  callbacks: {
    onSuccess: (response: RazorpaySubscriptionResponse) => void;
    onDismiss?: () => void;
    onError?: (error: unknown) => void;
  },
  /** See openRazorpayCheckout's descriptionOverride — same reason. */
  descriptionOverride?: string
): Promise<void> => {
  const loaded = await loadRazorpayScript();
  if (!loaded) {
    callbacks.onError?.(new Error("Failed to load Razorpay SDK"));
    return;
  }

  const options: RazorpayOptions = {
    key: subscriptionData.razorpay_key_id,
    amount: subscriptionData.amount,
    currency: subscriptionData.currency,
    name: "AXOIX",
    description: descriptionOverride || `${subscriptionData.tier_name} - recurring subscription`,
    subscription_id: subscriptionData.subscription_id,
    recurring: true,
    handler: callbacks.onSuccess as unknown as (response: RazorpayResponse | RazorpaySubscriptionResponse) => void,
    prefill: {
      name: userInfo.name,
      email: userInfo.email,
      contact: userInfo.phone,
    },
    notes: {
      tier: subscriptionData.tier_name,
    },
    redirect: false,
    theme: {
      color: "#00ff97",
      backdrop_color: "rgba(10, 25, 47, 0.9)",
    },
    modal: {
      ondismiss: callbacks.onDismiss,
      escape: false,
      animation: true,
      confirm_close: true,
    },
  };

  try {
    const razorpay = new window.Razorpay(options);
    // See the matching comment in openRazorpayCheckout above — a failed
    // charge inside the modal fires "payment.failed", not a thrown error.
    razorpay.on("payment.failed", (response) => {
      callbacks.onError?.(new Error(response.error?.description || "Payment failed"));
    });
    razorpay.open();
  } catch (error) {
    callbacks.onError?.(error);
  }
};

// ==================== PRICE FORMATTING ====================

/**
 * Format price for display (INR)
 */
export const formatPrice = (amount: number, currency: string = "INR"): string => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Get billing cycle display text
 */
export const getBillingCycleText = (cycle: string): string => {
  switch (cycle) {
    case "monthly":
      return "/month";
    case "quarterly":
      return "/3 months";
    case "six_month":
      return "/6 months";
    case "annual":
    case "yearly":
      return "/year";
    case "lifetime":
      return " (Lifetime)";
    default:
      return "";
  }
};

const billingService = {
  getPricingTiers,
  createOrder,
  verifyPayment,
  sandboxActivate,
  isSandboxMode,
  getSubscriptionStatus,
  getPaymentHistory,
  loadRazorpayScript,
  openRazorpayCheckout,
  formatPrice,
  getBillingCycleText,
};

export default billingService;
